import {
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    IconButton,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'

import { useCallback, useEffect, useState, useRef } from 'react'
import axiosInstance from '../../config/axiosConfig'
import { urlConfig } from '../../config/apiConfig'
import ModalRequest from '../../components/ModalRequest'

export default function SoundSetting({
    open,
    setOpen,
    row,
    handleAlert,
    refresh,
}) {
    const [loading, setLoading] = useState(false)
    const [sounds, setSounds] = useState('')
    const [data, setData] = useState({
        remain_sound: '',
        trigger_sound: '',
        enable_sound: false,
    })
    const [isPlaying, setIsPlaying] = useState(null) // Track the currently playing sound ID
    const audioRef = useRef(null) // Single ref for the audio element
    const [isDropdownOpen, setIsDropdownOpen] = useState(false) // Track dropdown open state

    const handlePlayPause = (sound) => {
        if (isPlaying === sound.sound_id) {
            // Stop the currently playing sound
            audioRef.current.pause()
            setIsPlaying(null)
        } else {
            // Play the selected sound
            if (audioRef.current) {
                audioRef.current.pause() // Stop any previously playing sound
            }
            audioRef.current = new Audio(sound.sound_url)
            audioRef.current.play()
            setIsPlaying(sound.sound_id)
            audioRef.current.onended = () => setIsPlaying(null)
        }
    }

    const getSoundById = useCallback(async (sound_id) => {
        try {
            const res = await axiosInstance.get(
                `${urlConfig.sound}/${sound_id}`,
                {
                    responseType: 'arraybuffer',
                },
            )
            if (res.data) {
                const blob = new Blob([res.data], { type: 'audio/wav' })
                return URL.createObjectURL(blob)
            }
            return null
        } catch (error) {
            return null
        }
    }, [])

    const getSound = useCallback(async () => {
        try {
            if (!open) {
                return
            }
            const res = await axiosInstance.get(urlConfig.sound)
            if (res.data.message && res.data.message.length > 0) {
                const soundData = await Promise.all(
                    res.data.message.map(async (item, index) => {
                        const sound_url = await getSoundById(item.sound_id)
                        return {
                            ...item,
                            id: index + 1,
                            sound_url,
                        }
                    }),
                )
                setSounds(soundData || [])
            } else {
                setSounds([])
            }
        } catch (error) {
            handleAlert(
                error.response?.data?.message || 'Failed to fetch sounds',
                'error',
            )
        }
    }, [open, handleAlert, getSoundById])

    const onSubmit = async () => {
        try {
            setLoading(true)
            if (row) {
                await axiosInstance.put(
                    `${urlConfig.organization}/${row.keyName}`,
                    {
                        remain_sound: data.remain_sound,
                        trigger_sound: data.trigger_sound,
                        enable_sound: data.enable_sound,
                        keyName: row.keyName,
                    },
                )
                handleAlert('Setting Sound', 'success')
            }
            setOpen(false)
            refresh()
            setData({
                remain_sound: '',
                trigger_sound: '',
                enable_sound: false,
            })
        } catch (error) {
            handleAlert(
                error.response?.data?.message || 'Failed to save Setting Sound',
                'error',
            )
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (row) {
            setData(row)
        } else {
            setData({ remain_sound: '', trigger_sound: '' })
        }
    }, [row, open])

    useEffect(() => {
        getSound()
    }, [getSound])

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause() // Stop the audio
                audioRef.current = null // Clear the audio reference
            }
        }
    }, [])

    return (
        <ModalRequest
            width='600px'
            disabled={loading}
            open={open}
            setOpen={setOpen}
            title={`Notification sound Setting`}
            onSubmit={onSubmit}
        >
            <Grid item xs={12} mt={1}>
                <FormControl fullWidth size='small'>
                    <InputLabel id='remain_sound'>Remain Sound</InputLabel>
                    <Select
                        required
                        fullWidth
                        label='Remain Sound'
                        name='remain_sound'
                        value={data?.remain_sound || ''}
                        onChange={(e) =>
                            setData({
                                ...data,
                                remain_sound: e.target.value,
                            })
                        }
                        onOpen={() => setIsDropdownOpen(true)}
                        onClose={() => setIsDropdownOpen(false)}
                        renderValue={(selected) => {
                            const selectedSound = Array.isArray(sounds)
                                ? sounds.find(
                                      (sound) => sound.sound_id === selected,
                                  )
                                : null
                            return selectedSound
                                ? selectedSound.name
                                : 'Select Sound'
                        }}
                    >
                        <MenuItem value=''>Select Sound</MenuItem>
                        {!sounds && (
                            <MenuItem value={data?.remain_sound}>
                                Loading...
                            </MenuItem>
                        )}
                        {Array.isArray(sounds) &&
                            sounds.map((sound) => (
                                <MenuItem
                                    key={sound.sound_id}
                                    value={sound.sound_id}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    {sound.name}
                                    {isDropdownOpen && (
                                        <IconButton
                                            size='small'
                                            onClick={(e) => {
                                                e.stopPropagation() // Prevent dropdown from closing
                                                handlePlayPause(sound)
                                            }}
                                            sx={{
                                                color: '#919191',
                                                background: '#dbdbdb',
                                            }}
                                        >
                                            {isPlaying === sound.sound_id ? (
                                                <StopIcon />
                                            ) : (
                                                <PlayArrowIcon />
                                            )}
                                        </IconButton>
                                    )}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <FormControl fullWidth size='small'>
                    <InputLabel id='trigger_sound'>Trigger Sound</InputLabel>
                    <Select
                        required
                        fullWidth
                        label='Trigger Sound'
                        name='trigger_sound'
                        value={data?.trigger_sound || ''}
                        onChange={(e) =>
                            setData({
                                ...data,
                                trigger_sound: e.target.value,
                            })
                        }
                        onOpen={() => setIsDropdownOpen(true)}
                        onClose={() => setIsDropdownOpen(false)}
                        renderValue={(selected) => {
                            const selectedSound = Array.isArray(sounds)
                                ? sounds.find(
                                      (sound) => sound.sound_id === selected,
                                  )
                                : null
                            return selectedSound
                                ? selectedSound.name
                                : 'Select Sound'
                        }}
                    >
                        <MenuItem value=''>Select Sound</MenuItem>
                        {!sounds && (
                            <MenuItem value={data?.remain_sound}>
                                Loading...
                            </MenuItem>
                        )}
                        {Array.isArray(sounds) &&
                            sounds.map((sound) => (
                                <MenuItem
                                    key={sound.sound_id}
                                    value={sound.sound_id}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    {sound.name}
                                    {isDropdownOpen && (
                                        <IconButton
                                            size='small'
                                            onClick={(e) => {
                                                e.stopPropagation() // Prevent dropdown from closing
                                                handlePlayPause(sound)
                                            }}
                                            sx={{
                                                color: '#919191',
                                                background: '#dbdbdb',
                                            }}
                                        >
                                            {isPlaying === sound.sound_id ? (
                                                <StopIcon />
                                            ) : (
                                                <PlayArrowIcon />
                                            )}
                                        </IconButton>
                                    )}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <label>Enable</label>
                <Switch
                    color='success'
                    checked={data?.enable_sound || false}
                    onChange={(e) =>
                        setData({
                            ...data,
                            enable_sound: e.target.checked,
                        })
                    }
                />
            </Grid>
        </ModalRequest>
    )
}
